import * as Sentry from '@sentry/browser';
import type { BaseTransportOptions, Transport, TransportMakeRequestResponse } from '@sentry/core';

/* Scripts: Parts */
import header from '@/scripts/parts/header';
import navigation from '@/scripts/parts/navigation';
import footer from '@/scripts/parts/footer';
import search from '@/scripts/parts/search';

/* Scripts: Modules */
import accordionGroup from '@/scripts/modules/accordion-group';
import artistDirectory from '@/scripts/modules/artist-directory';
import divider from '@/scripts/modules/divider';
import event from '@/scripts/modules/event';
import events from '@/scripts/modules/events';
import panels from '@/scripts/modules/panels';
import eventsList from '@/scripts/modules/events-list';
import gallerySlider from '@/scripts/modules/gallery-slider';
import hero from '@/scripts/modules/hero';
import heroFeature from '@/scripts/modules/hero-feature';
import news from '@/scripts/modules/news';
import newsList from '@/scripts/modules/news-list';
import peopleSearch from '@/scripts/modules/people-search';
import peopleFeatured from '@/scripts/modules/people-featured';
import alumniSearch from '@/scripts/modules/alumni-search';
import worksSearch from '@/scripts/modules/works-search';
import quotes from '@/scripts/modules/quotes';
import sectionSlider from '@/scripts/modules/section-slider';
import storytelling from '@/scripts/modules/storytelling';

/* Scripts: Elements */
import designs from '@/scripts/elements/designs';
import typography from '@/scripts/elements/typography';
import iframes from '@/scripts/elements/iframes';

/* Initialize: Parts */
header.init();
navigation.init();
footer.init();
search.init();

/* Initialize: Modules */
accordionGroup.init();
artistDirectory.init();
divider.init();
event.init();
events.init();
eventsList.init();
gallerySlider.init();
hero.init();
heroFeature.init();
news.init();
newsList.init();
peopleFeatured.init();
peopleSearch.init();
alumniSearch.init();
worksSearch.init();
quotes.init();
sectionSlider.init();
panels.init();
storytelling.init();

/* Initialize: Elements */
designs.init();
typography.init();
iframes.init();

/* Styles */
import './styles/main.scss';
import '@splidejs/splide/dist/css/splide.min.css';
import 'slick-carousel/slick/slick.css';

/* Assets */
import './fonts/Curtis-Regular.otf';

interface WordPressTransportOptions extends BaseTransportOptions {
  url: string;
}

function createWordPressTransport(options: WordPressTransportOptions): Transport {
  return Sentry.createTransport(options, async function makeRequest(request): Promise<TransportMakeRequestResponse> {
    const url = options.url;

    // Get the nonce from your localized script
    const nonce = window.curtis.security;

    // Prepare headers
    const headers: Record<string, string> = {
      'Content-Type': 'application/x-sentry-envelope',
    };

    // Add nonce if available
    if (nonce) {
      headers['X-WP-Nonce'] = nonce;
    }

    // Make the actual request with error handling
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: typeof request.body === 'string' ? request.body : new Uint8Array(request.body),
      });

      return {
        statusCode: response.status,
        headers: {
          'x-sentry-rate-limits': response.headers.get('X-Sentry-Rate-Limits'),
          'retry-after': response.headers.get('Retry-After'),
        },
      };
    } catch (error) {
      console.error('Sentry transport request failed:', error);
      return {
        statusCode: 500,
        headers: {
          'x-sentry-rate-limits': null,
          'retry-after': null,
        },
      };
    }
  });
}

/* Ready */
window.addEventListener('DOMContentLoaded', () => {
  console.log('Assets Loaded');

  Sentry.setUser({
    username: window.curtis.user_name ?? '',
    email: window.curtis.user_email ?? '',
  });

  // Set global tags to help with filtering
  Sentry.setTag('environment', window.curtis.environment);
  Sentry.setTag('theme_version', window.curtis.version);
  Sentry.setTag('site', 'curtis.edu');

  Sentry.init({
    dsn: window.curtis.sentry,
    environment: window.curtis.environment,
    release: window.curtis.version,
    transport: (options: BaseTransportOptions): Transport => createWordPressTransport({
      ...options,
      url: `${window.curtis.apiurl}/tunnel`,
    }),
    sendDefaultPii: true,
    tracesSampleRate: window.curtis.environment === 'local' ? 0.1 : 1.0,
    ignoreErrors: [
      /* Ignoring Sentry session replay error */
      /extractFilteredSchemaValuesFromMicroData.*readonly property/,

      /* Ignoring New Relic */
      /newrelic/i,
      /nrWrapper/i,
      /nr-script/i,

      /* Ignoring elfsight.com */
      /elfsight.com/i,

      /* Ignoring browser extension errors */
      /Invalid call to runtime\.sendMessage\(\)/,
      /Extension context invalidated/,
      /chrome-extension:/,
      /moz-extension:/,
      /safari-extension:/,
      /Could not establish connection\. Receiving end does not exist/,

      /* Ignoring WebView/mobile browser errors */
      /Java object is gone/,
      /JavaBridge/i,
      /Non-Error promise rejection captured with keys:/,

      /* Ignoring iOS WebKit errors in third-party browsers */
      /window\.webkit\.messageHandlers/,
      /webkit\.messageHandlers/,

      /* Ignoring React Native errors */

      /* Ignoring third-party service worker errors */
      /ServiceWorker.*aimtell/i,
      /aimtell-worker\.js/i,
      /Failed to register a ServiceWorker/,

      /* Ignoring third-party video player errors */
      /The player element passed isn't a Vimeo embed/,
      /Vimeo.*player/i,

      /* Ignoring media/autoplay policy errors */
      /NotAllowedError.*play/i,
      /NotSupportedError.*sources/i,
      /AbortError.*play.*pause/i,

      /* Ignoring generic third-party errors */
      /Script error/,
      /Non-Error exception captured/,
      /ResizeObserver loop limit exceeded/,
    ],
    integrations: [
      ...(window.curtis.environment === 'local' ? [] : [
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        })
      ]),
      Sentry.feedbackIntegration({
        autoInject: false,
        colorScheme: 'dark',
        triggerLabel: 'Report an Issue',
        formTitle: 'Report an Issue',
        addScreenshotButtonLabel: 'Add a Screenshot',
        submitButtonLabel: 'Send Feedback',
      }),
      Sentry.consoleLoggingIntegration({ levels: [ 'error' ] }),
    ],
    replaysSessionSampleRate: window.curtis.environment === 'local' ? 0.0 : 0.1,
    replaysOnErrorSampleRate: window.curtis.environment === 'local' ? 0.0 : 1.0,
    _experiments: { enableLogs: true },

    beforeSend(event) {
      // Filter out third-party script errors
      if (event.exception?.values) {
        for (const exception of event.exception.values) {
          const stacktrace = exception.stacktrace;
          if (stacktrace?.frames) {
            // Check if error originates from third-party domains
            const hasThirdPartyFrames = stacktrace.frames.some(frame => {
              const filename = frame.filename || '';
              console.log({ errorSrc: filename });
              return (
                filename.includes('vimeo.com') ||
                filename.includes('player.js') ||
                filename.includes('aimtell') ||
                filename.includes('facebook.net') ||
                filename.includes('google') ||
                filename.includes('newrelic') ||
                filename.includes('elfsight') ||
                filename.includes('chrome-extension') ||
                filename.includes('moz-extension') ||
                filename.includes('safari-extension')
              );
            });

            if (hasThirdPartyFrames) {
              return null; // Don't send to Sentry
            }
          }
        }
      }

      // Filter out errors from specific URLs that are known to be problematic
      if (event.request?.url) {
        const url = event.request.url;
        if (
          url.includes('webkit-masked-url') ||
          url.includes('chrome-extension') ||
          url.includes('moz-extension')
        ) {
          return null;
        }
      }

      return event;
    },
  });

  if (window.curtis.user_email) {
    const feedback = Sentry.getFeedback();
    feedback?.createWidget();
  }
});
